/**
 * English: Fetch object-library items (type -> preview/state images) from the
 *          Limbic maker API, using the logged-in admin token.
 * Thai: ดึง object-library (type -> รูป) จาก maker API ด้วย token ของแอดมิน
 */
import { useState, useEffect } from 'react';
import { MAKER_API_BASE } from '../config/api';
import { useAuth } from '../context/AuthContext';

export interface ObjectLibraryItem {
  id: string;
  category: string;
  type: string;
  name: string;
  preview_image: string;
  state_images?: Record<string, string>;
}

export interface UseObjectLibraryResult {
  items: ObjectLibraryItem[];
  /** true once the fetch has settled (success OR failure) — used to gate rendering. */
  settled: boolean;
}

export function useObjectLibrary(): UseObjectLibraryResult {
  const { token } = useAuth();
  const [items, setItems] = useState<ObjectLibraryItem[]>([]);
  const [settled, setSettled] = useState(false);

  useEffect(() => {
    if (!token) {
      setSettled(true);
      return;
    }
    let cancelled = false;
    setSettled(false);
    const url = `${MAKER_API_BASE}/api/object-library`;
    fetch(url, { headers: { Accept: 'application/json', Authorization: `Bearer ${token}` } })
      .then((res) => (res.ok ? res.json() : Promise.reject(new Error(`HTTP ${res.status}`))))
      .then((data: unknown) => {
        if (!cancelled && Array.isArray(data)) setItems(data as ObjectLibraryItem[]);
      })
      .catch(() => {
        if (!cancelled) setItems([]);
      })
      .finally(() => {
        if (!cancelled) setSettled(true);
      });
    return () => {
      cancelled = true;
    };
  }, [token]);

  return { items, settled };
}
